/**
 * Consent Service
 *
 * Manages user consent for cookies/tracking in compliance with GDPR/CCPA.
 *
 * Consent Categories:
 * - essential: Required for site functionality (always enabled, no consent needed)
 * - analytics: Performance and usage tracking (requires consent)
 * - marketing: Advertising and personalization (requires consent)
 *
 * Storage: localStorage with 'arterra_consent' key
 * Expiry: 365 days from consent given
 */

import { devLog, devWarn } from '@/utils/devLog';

// Declare gtag for Consent Mode v2 updates
declare function gtag(...args: unknown[]): void;

/**
 * Check if localStorage is available (handles private browsing mode)
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__arterra_storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// Consent categories
export type ConsentCategory = 'essential' | 'analytics' | 'marketing';

// Consent state for each category
export interface ConsentState {
  essential: boolean;  // Always true, required for site to function
  analytics: boolean;  // Performance tracking, Firebase Analytics
  marketing: boolean;  // Future: advertising, remarketing
}

// Full consent record stored in localStorage
export interface ConsentRecord {
  state: ConsentState;
  timestamp: number;      // When consent was given/updated
  version: string;        // Consent policy version (for re-consent on policy changes)
  expiresAt: number;      // When consent expires (365 days)
}

// Current consent policy version - increment when policy changes significantly
const CONSENT_POLICY_VERSION = '1.0.0';

// Consent expiry in milliseconds (365 days)
const CONSENT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

// localStorage key
const CONSENT_STORAGE_KEY = 'arterra_consent';

// Default consent state (before user makes a choice)
const DEFAULT_CONSENT_STATE: ConsentState = {
  essential: true,   // Always enabled
  analytics: false,  // Disabled until consent
  marketing: false,  // Disabled until consent
};

class ConsentService {
  private listeners: Set<(state: ConsentState) => void> = new Set();
  private currentState: ConsentState = { ...DEFAULT_CONSENT_STATE };
  private hasUserConsented: boolean = false;

  constructor() {
    // Set Consent Mode v2 defaults — all denied until explicit user consent
    try {
      if (typeof gtag === 'function') {
        gtag('consent', 'default', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      }
    // FIX-R6-19: Log instead of bare empty catch (CCS-3 violation)
    } catch (err) {
      devWarn('[ConsentService] gtag not available for consent defaults:', err);
    }

    this.loadConsent();
  }

  /**
   * Load consent from localStorage
   */
  private loadConsent(): void {
    if (!isLocalStorageAvailable()) {
      devWarn('[ConsentService] localStorage not available (private browsing?)');
      return;
    }

    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!stored) {
        devLog('[ConsentService] No stored consent found');
        return;
      }

      const record: ConsentRecord = JSON.parse(stored);

      // Validate the shape of the parsed consent record
      if (
        !record ||
        typeof record !== 'object' ||
        !record.state ||
        typeof record.state.analytics !== 'boolean' ||
        typeof record.state.essential !== 'boolean'
      ) {
        devWarn('[ConsentService] Invalid consent record shape, clearing');
        this.clearConsent();
        return;
      }

      // Check if consent has expired
      if (Date.now() > record.expiresAt) {
        devLog('[ConsentService] Consent has expired, clearing');
        this.clearConsent();
        return;
      }

      // Check if policy version has changed (would require re-consent)
      if (record.version !== CONSENT_POLICY_VERSION) {
        devLog('[ConsentService] Policy version changed, clearing consent');
        this.clearConsent();
        return;
      }

      // Ensure essential is always true
      record.state.essential = true;
      this.currentState = record.state;
      this.hasUserConsented = true;

      devLog('[ConsentService] Loaded consent:', this.currentState);
    } catch (error) {
      devWarn('[ConsentService] Error loading consent:', error);
      this.clearConsent();
    }
  }

  /**
   * Save consent to localStorage
   */
  private saveConsent(): void {
    if (!isLocalStorageAvailable()) {
      devWarn('[ConsentService] Cannot save consent - localStorage not available');
      return;
    }

    try {
      const record: ConsentRecord = {
        state: this.currentState,
        timestamp: Date.now(),
        version: CONSENT_POLICY_VERSION,
        expiresAt: Date.now() + CONSENT_EXPIRY_MS,
      };

      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
      devLog('[ConsentService] Saved consent:', record);
    } catch (error) {
      devWarn('[ConsentService] Error saving consent:', error);
    }
  }

  /**
   * Clear stored consent
   */
  private clearConsent(): void {
    this.currentState = { ...DEFAULT_CONSENT_STATE };
    this.hasUserConsented = false;

    if (!isLocalStorageAvailable()) {
      return;
    }

    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch (error) {
      devWarn('[ConsentService] Error clearing consent:', error);
    }
  }

  /**
   * Get current consent state
   */
  getConsentState(): ConsentState {
    return { ...this.currentState };
  }

  /**
   * Check if user has made a consent choice
   */
  hasConsented(): boolean {
    return this.hasUserConsented;
  }

  /**
   * Check if a specific category is consented
   */
  hasConsentFor(category: ConsentCategory): boolean {
    if (category === 'essential') return true;
    return this.hasUserConsented && this.currentState[category];
  }

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    this.currentState = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    this.hasUserConsented = true;
    this.saveConsent();
    this.updateGtagConsent();
    this.notifyListeners();
    devLog('[ConsentService] Accepted all');
  }

  /**
   * Reject all optional cookies (keep essential only)
   */
  rejectAll(): void {
    this.currentState = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    this.hasUserConsented = true;
    this.saveConsent();
    this.updateGtagConsent();
    this.notifyListeners();
    devLog('[ConsentService] Rejected all optional');
  }

  /**
   * Set custom consent preferences
   */
  setPreferences(preferences: Partial<Omit<ConsentState, 'essential'>>): void {
    this.currentState = {
      essential: true, // Always true
      analytics: preferences.analytics ?? false,
      marketing: preferences.marketing ?? false,
    };
    this.hasUserConsented = true;
    this.saveConsent();
    this.updateGtagConsent();
    this.notifyListeners();
    devLog('[ConsentService] Set preferences:', this.currentState);
  }

  /**
   * Update Google Consent Mode v2 based on current consent state
   */
  private updateGtagConsent(): void {
    try {
      if (typeof gtag === 'function') {
        gtag('consent', 'update', {
          analytics_storage: this.currentState.analytics ? 'granted' : 'denied',
          ad_storage: this.currentState.marketing ? 'granted' : 'denied',
          ad_user_data: this.currentState.marketing ? 'granted' : 'denied',
          ad_personalization: this.currentState.marketing ? 'granted' : 'denied',
        });
        devLog('[ConsentService] Consent Mode v2 updated:', this.currentState);
      }
    } catch (error) {
      devWarn('[ConsentService] Failed to update gtag consent:', error);
    }
  }

  /**
   * Withdraw consent (clear all)
   */
  withdrawConsent(): void {
    this.clearConsent();
    this.updateGtagConsent();
    this.notifyListeners();
    devLog('[ConsentService] Consent withdrawn');
  }

  /**
   * Subscribe to consent state changes
   */
  subscribe(listener: (state: ConsentState) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    const state = this.getConsentState();
    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        devWarn('[ConsentService] Error in listener:', error);
      }
    });
  }

  /**
   * Get consent record for debugging/compliance
   */
  getConsentRecord(): ConsentRecord | null {
    if (!isLocalStorageAvailable()) {
      return null;
    }

    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }
}

// Singleton instance
export const consentService = new ConsentService();

// Export class for testing
export { ConsentService };
